import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { Radio, RadioGroup, useTheme, useMediaQuery } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";
import { reportAdsConversion } from "src/libs/analytics";
import { PricingPlan } from "src/sections/pricing/pricing-plan";
import { PricingPlanIcon } from "src/sections/pricing/pricing-plan-icon";
import CheckIcon from "@untitled-ui/icons-react/build/esm/Check";
import SvgIcon from "@mui/material/SvgIcon";
import WarningIcon from "@mui/icons-material/Warning";

const Page = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  usePageView();
  const [currency, setCurrency] = useState("€");
  const [payment, setPayment] = useState<"monthly" | "yearly">("monthly");

  const gtag_report_conversion = (_url?: string) => {
    reportAdsConversion("AW-1026266099/wcX-COGByLADEPOnrukD", {
      transaction_id: "",
    });
  };

  const services = [
    "Complete loop setup & configuration (2–4 hours)",
    "Device pairing & troubleshooting (CGM, pump, phone, smartwatch)",
    "Nightscout deployment & optimization",
    "Loop configuration review & adjustment",
    "Daily glucose monitoring & optimization advice",
    "24/7 emergency support for critical issues",
    "Regular check-ins & performance reports",
  ];

  return (
    <>
      <Seo
        title="Loop Setup and Support"
        description="Expert setup for your open-source loop: CGM, pump, phone, and Nightscout configuration, plus follow-up support so daily diabetes care takes less effort."
        type="website"
      />
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "neutral.800" : "neutral.50",
            pt: 15,
          }}
        >
          <Container maxWidth="lg">
            {/* Hero Section with Image */}
            <Grid container spacing={4} sx={{ mb: 8 }} alignItems="center">
              <Grid xs={12} md={6}>
                <Stack spacing={3}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: smDown ? "center" : "flex-start",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src="/assets/landingspage/l2.webp"
                      alt="Active lifestyle with diabetes management"
                      sx={{
                        width: "100%",
                        maxWidth: "600px",
                        borderRadius: "16px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                </Stack>
              </Grid>
              <Grid xs={12} md={6}>
                <Stack spacing={2}>
                  <Typography
                    variant="h3"
                    sx={{
                      textAlign: smDown ? "center" : "left",
                      fontWeight: "bold",
                      color: theme.palette.primary.main,
                    }}
                  >
                    {t("Loop Setup & Support Service")}
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ textAlign: smDown ? "center" : "left", mt: 2 }}
                  >
                    {t(
                      "Take advantage of our expertise in open-source diabetes management so you can focus on living your life."
                    )}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>

            {/* Pain Points Section */}
            <Box sx={{ mb: 8 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  mb: 4,
                  textAlign: "center",
                  color: theme.palette.primary.main,
                }}
              >
                {t("Challenges of setting up a loop yourself")}
              </Typography>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid xs={12} md={6}>
                  <Card
                    sx={{
                      height: "100%",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "neutral.900"
                          : "background.paper",
                      border: `1px solid ${theme.palette.error.main}20`,
                    }}
                  >
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <WarningIcon sx={{ color: "error.main" }} />
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", color: "error.main" }}
                          >
                            {t("Technical Complexity")}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "Building the loop app requires following complex step-by-step manuals. "
                          )}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} md={6}>
                  <Card
                    sx={{
                      height: "100%",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "neutral.900"
                          : "background.paper",
                      border: `1px solid ${theme.palette.error.main}20`,
                    }}
                  >
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <WarningIcon sx={{ color: "error.main" }} />
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", color: "error.main" }}
                          >
                            {t("Device Pairing Troubles")}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "Connecting your CGM, pump, and phone can be frustrating. Device pairing issues are common, and troubleshooting requires technical knowledge that many don't have."
                          )}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} md={6}>
                  <Card
                    sx={{
                      height: "100%",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "neutral.900"
                          : "background.paper",
                      border: `1px solid ${theme.palette.error.main}20`,
                    }}
                  >
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <WarningIcon sx={{ color: "error.main" }} />
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", color: "error.main" }}
                          >
                            {t("Configuration Overwhelm")}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "You need to set and adjust all settings yourself—safety parameters, insulin sensitivity, carb ratios, and more. This requires deep knowledge and continuous oversight that can be overwhelming."
                          )}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} md={6}>
                  <Card
                    sx={{
                      height: "100%",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "neutral.900"
                          : "background.paper",
                      border: `1px solid ${theme.palette.error.main}20`,
                    }}
                  >
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <WarningIcon sx={{ color: "error.main" }} />
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", color: "error.main" }}
                          >
                            {t("No Professional Support")}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "Unlike commercial systems, there's no 24/7 help line. When something goes wrong—especially critical issues—you're on your own to figure it out or rely on community forums."
                          )}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} md={6}>
                  <Card
                    sx={{
                      height: "100%",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "neutral.900"
                          : "background.paper",
                      border: `1px solid ${theme.palette.error.main}20`,
                    }}
                  >
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <WarningIcon sx={{ color: "error.main" }} />
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", color: "error.main" }}
                          >
                            {t("Nightscout Setup")}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "Deploying and managing Nightscout requires technical skills. Whe you choose for self-hosting you need to configure and mantain a webserver and perform software updatesr."
                          )}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} md={6}>
                  <Card
                    sx={{
                      height: "100%",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "neutral.900"
                          : "background.paper",
                      border: `1px solid ${theme.palette.error.main}20`,
                    }}
                  >
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <WarningIcon sx={{ color: "error.main" }} />
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", color: "error.main" }}
                          >
                            {t("Ongoing Optimization")}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "Continuous optimization requires learning and staying updated. Your healthcare provider can help with basics, but detailed configuration choices are often up to you alone."
                          )}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 6,
                  mb: 4,
                }}
              ></Box>
            </Box>

            {/* What We Offer Section */}
            <Box
              sx={{
                mb: 8,
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "center" }, // alignItems center on desktop
                justifyContent: "center",
                gap: 4,
                width: "100%",
              }}
            >
              {/* What We Offer - Left Panel */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center", // centers vertically if right panel taller
                  height: { xs: "auto", md: "100%" },
                  px: { xs: 0, md: 2 },
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    mb: 4,
                    textAlign: "center",
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("What We Offer")}
                </Typography>
                <Stack spacing={2}>
                  {services.map((service) => (
                    <Stack
                      key={service}
                      alignItems="center"
                      direction="row"
                      spacing={2}
                    >
                      <SvgIcon color="success">
                        <CheckIcon />
                      </SvgIcon>
                      <Typography sx={{ fontWeight: 500 }} variant="body1">
                        {t(service)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              {/* Image - Right Panel */}
              <Box
                component="img"
                src="/assets/landingspage/3.webp"
                alt="Life with diabetes management"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: "400px",
                  width: "100%",
                  height: "520px", // doubled explicit height for taller rectangular shape
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  objectFit: "cover",
                  objectPosition: "center",
                  display: { xs: "none", md: "block" },
                  ml: { md: 4 },
                }}
              />
            </Box>

            {/* Currency Selector */}
            <Stack
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                pb: 5,
              }}
              spacing={2}
            ></Stack>

            {/* Pricing Plans */}
            <Grid container spacing={4}>
              <Grid xs={12} md={6}>
                <PricingPlan
                  cta={t("Get Started")}
                  currency={currency}
                  payment={payment}
                  description={t(
                    "All-inclusive support with predictable weekly fee. Perfect for peace of mind."
                  )}
                  features={[
                    t("Covers technical loop setup and configuration"),
                    t("Covers medical coaching and stand by support"),
                    t(
                      "Device pairing & troubleshooting (CGM, pump, phone, smartwatch)"
                    ),
                    t("Nightscout setup, hosting and upgrades for 1 year"),
                    t("Daily glucose monitoring & optimization advice"),
                    t("24/7 emergency support for critical issues"),
                    t("Unlimited requests"),
                    t("Regular check-ins & performance reports"),
                  ]}
                  icon={<PricingPlanIcon name="business" />}
                  name={t("Flat Weekly Fee")}
                  textPrice={`${currency}1200/week`}
                  sx={{
                    height: "100%",
                    maxWidth: 460,
                    mx: "auto",
                  }}
                  link="https://cal.com/team/opensourcelinic/onboarding"
                  onCtaClick={(url) => gtag_report_conversion(url)}
                  popular
                />
              </Grid>
              <Grid xs={12} md={6}>
                <PricingPlan
                  cta={t("Get Started")}
                  currency={currency}
                  payment={payment}
                  description={t(
                    "Base fee with flexible on-demand hours. Ideal for ongoing optimization."
                  )}
                  features={[
                    t("Covers technical loop setup and configuration"),
                    t("Nightscout setup, hosting and upgrades for 1 year"),
                    t("Daily glucose monitoring & optimization advice"),
                    t(
                      `Additional medical coaching hours billed at 50 EUR/hour`
                    ),
                  ]}
                  icon={<PricingPlanIcon name="standard" />}
                  name={t("Base Fee + On-Demand")}
                  textPrice={`${currency}600/week  + ${currency}50/hour`}
                  sx={{
                    height: "100%",
                    maxWidth: 460,
                    mx: "auto",
                  }}
                  link="https://cal.com/team/opensourcelinic/onboarding"
                  onCtaClick={(url) => gtag_report_conversion(url)}
                />
              </Grid>
            </Grid>
          </Container>
        </Box>
        <Divider />
      </Box>
    </>
  );
};

export default Page;
